// System-Werte, die NICHT in /v2-api/settings/global liegen
// (docs/einstellungen-inventar.md, Schritt 3).
//
// Warum nicht einfach alles in die Settings-API kippen: diese Werte
// gehoeren einer laufenden Domaene. Die Blitz-Radien haelt der
// MQTT-Client in seiner eigenen Konfiguration, die Batterie-Schwelle
// der HA-Client -- beide Endpunkte laden nach dem Schreiben neu. Wer
// den Wert an der Domaene vorbei in die Settings-Tabelle schriebe,
// haette ihn gespeichert und trotzdem nichts geaendert: der laufende
// Client liefe bis zum naechsten Neustart mit dem alten weiter.
//
// Deshalb schreibt jeder Wert auf den Endpunkt SEINER Domaene. Das
// Manifest sieht davon nichts, es ruft lies() und schreib().
//
// Wie im System-Store gilt: die ANTWORT ist die Wahrheit, nicht der
// gesendete Wert -- beide Endpunkte klemmen (Track 10-500, Alert
// 5-200, Cooldown 1-1440, Batterie 1-100).

import { getJSON, postJSON, patchJSON } from "../api";

// Die drei Dienst-Verbindungen. Das KENNWORT steht hier bewusst nicht:
// es liegt im Geheimnis-Speicher, der Settings-Block haelt nur die
// Referenz (internal/api/verbindungs_kennwort.go). Die Oberflaeche
// erfaehrt nur, OB eines hinterlegt ist -- password_set.
export interface MqttWerte {
  enabled: boolean;
  broker: string;
  port: number;
  username: string;
  password_set: boolean;
}

export interface AdGuardWerte {
  url: string;
  username: string;
  password_set: boolean;
}

export interface AcInfinityWerte {
  email: string;
  poll_interval: number;
  password_set: boolean;
}

export interface BlitzWerte {
  track_radius_km: number;
  alert_radius_km: number;
  alert_cooldown_min: number;
}

let blitz = $state<BlitzWerte | null>(null);
let mqtt = $state<MqttWerte | null>(null);
let adguard = $state<AdGuardWerte | null>(null);
let acinfinity = $state<AcInfinityWerte | null>(null);
let batterie = $state<number | null>(null);
let fehler = $state("");
let gestartet = false;

export function getBlitzWerte(): BlitzWerte | null {
  return blitz;
}
export function getBatterieStandard(): number | null {
  return batterie;
}
export function getMqttWerte(): MqttWerte | null {
  return mqtt;
}
export function getAdGuardWerte(): AdGuardWerte | null {
  return adguard;
}
export function getAcInfinityWerte(): AcInfinityWerte | null {
  return acinfinity;
}
export function getDomaenenFehler(): string {
  return fehler;
}

async function hole(): Promise<void> {
  try {
    const [l, b, m, a, c] = await Promise.all([
      getJSON<BlitzWerte>("/v2-api/lightning"),
      getJSON<{ default_threshold: number }>("/v2-api/batteries/threshold-default"),
      getJSON<MqttWerte>("/v2-api/settings/mqtt"),
      getJSON<AdGuardWerte>("/v2-api/settings/adguard"),
      getJSON<AcInfinityWerte>("/v2-api/settings/acinfinity"),
    ]);
    mqtt = m;
    adguard = a;
    acinfinity = c;
    blitz = {
      track_radius_km: l.track_radius_km,
      alert_radius_km: l.alert_radius_km,
      alert_cooldown_min: l.alert_cooldown_min,
    };
    batterie = b.default_threshold;
    fehler = "";
  } catch (e) {
    fehler = e instanceof Error ? e.message : String(e);
  }
}

/** Einmal laden. Idempotent; kein Polling -- diese Werte aendert nur,
 *  wer sie hier aendert. */
export function startDomaenenWerte(): void {
  if (gestartet) return;
  gestartet = true;
  void hole();
}

export async function setzeBlitzWert<K extends keyof BlitzWerte>(
  feld: K,
  wert: number,
): Promise<void> {
  if (!blitz) return;
  const vorher = blitz[feld];
  blitz = { ...blitz, [feld]: wert };
  try {
    // Der PATCH liefert die vollstaendige Konfiguration zurueck -- schon
    // geklemmt. Genau die uebernehmen wir.
    const frisch = await patchJSON<BlitzWerte>("/v2-api/lightning/config", {
      [feld]: wert,
    });
    blitz = {
      track_radius_km: frisch.track_radius_km,
      alert_radius_km: frisch.alert_radius_km,
      alert_cooldown_min: frisch.alert_cooldown_min,
    };
    fehler = "";
  } catch (e) {
    blitz = { ...blitz, [feld]: vorher };
    fehler = e instanceof Error ? e.message : String(e);
    void hole();
    throw e;
  }
}

export async function setzeBatterieStandard(wert: number): Promise<void> {
  const vorher = batterie;
  batterie = wert;
  try {
    const frisch = await postJSON<{ default_threshold: number }>(
      "/v2-api/batteries/threshold-default",
      { threshold: wert },
    );
    batterie = frisch.default_threshold;
    fehler = "";
  } catch (e) {
    batterie = vorher;
    fehler = e instanceof Error ? e.message : String(e);
    void hole();
    throw e;
  }
}

// ── Die drei Verbindungen ───────────────────────────────────────────
//
// Ein Helfer je Block statt einer Verallgemeinerung: die Endpunkte
// heissen unterschiedlich, und drei kurze Funktionen sind leichter zu
// lesen als eine, die alles kann.

async function schreibVerbindung<T extends object>(
  pfad: string,
  feld: string,
  wert: unknown,
  setze: (frisch: T) => void,
  zurueck: () => void,
): Promise<void> {
  try {
    // Wie ueberall hier: die ANTWORT ist die Wahrheit. Das Backend
    // klemmt (Poll-Intervall 5-600) und normiert Text.
    setze(await patchJSON<T>(pfad, { [feld]: wert }));
    fehler = "";
  } catch (e) {
    zurueck();
    fehler = e instanceof Error ? e.message : String(e);
    void hole();
    throw e;
  }
}

export function setzeMqttWert<K extends keyof MqttWerte>(
  feld: K,
  wert: MqttWerte[K],
): Promise<void> {
  if (!mqtt) return Promise.resolve();
  const vorher = mqtt;
  mqtt = { ...mqtt, [feld]: wert };
  return schreibVerbindung<MqttWerte>(
    "/v2-api/settings/mqtt",
    feld,
    wert,
    (frisch) => (mqtt = frisch),
    () => (mqtt = vorher),
  );
}

export function setzeAdGuardWert<K extends keyof AdGuardWerte>(
  feld: K,
  wert: AdGuardWerte[K],
): Promise<void> {
  if (!adguard) return Promise.resolve();
  const vorher = adguard;
  adguard = { ...adguard, [feld]: wert };
  return schreibVerbindung<AdGuardWerte>(
    "/v2-api/settings/adguard",
    feld,
    wert,
    (frisch) => (adguard = frisch),
    () => (adguard = vorher),
  );
}

export function setzeAcInfinityWert<K extends keyof AcInfinityWerte>(
  feld: K,
  wert: AcInfinityWerte[K],
): Promise<void> {
  if (!acinfinity) return Promise.resolve();
  const vorher = acinfinity;
  acinfinity = { ...acinfinity, [feld]: wert };
  return schreibVerbindung<AcInfinityWerte>(
    "/v2-api/settings/acinfinity",
    feld,
    wert,
    (frisch) => (acinfinity = frisch),
    () => (acinfinity = vorher),
  );
}

// ── Verbindungen pruefen ────────────────────────────────────────────
//
// Eine Handlung, kein Wert: sie aendert nichts, sie fragt nach. Die
// beiden Endpunkte antworten mit {ok, message} und pruefen gegen die
// GESPEICHERTEN Zugangsdaten -- das Kennwort loest der Server aus dem
// Geheimnis-Speicher auf, die Oberflaeche bekommt es nie zu sehen.

export interface Pruefung {
  ok: boolean;
  text: string;
}

async function pruefe(pfad: string): Promise<Pruefung> {
  try {
    const a = await postJSON<{ ok: boolean; message?: string }>(pfad, {});
    return { ok: a.ok, text: a.message || (a.ok ? "Verbindung steht." : "Keine Verbindung.") };
  } catch (e) {
    return { ok: false, text: e instanceof Error ? e.message : String(e) };
  }
}

export function pruefeAdGuard(): Promise<Pruefung> {
  return pruefe("/v2-api/settings/adguard/test");
}

export function pruefeAcInfinity(): Promise<Pruefung> {
  return pruefe("/v2-api/settings/acinfinity/test");
}
